<script setup>
// X216 (COUNTRY_PLAN.md §4.3-4.4, Grant 2026-10-28: "as simple as possible"): "Where is {club} based?" for one club or
// for every club an admin runs that has no country, in ONE dialog. One tap when there is a suggestion: "Is {club} in
// {country}?" Yes / Choose another; for several clubs suggested the same country, "Are all N clubs in {country}?"
// Yes, all of them, with the rows underneath to change any one first. A row with no suggestion is empty and must be
// filled (never the first country in the list). Not now = 24 hours on the admin's account.
//   <club-country-dialog v-model="open" :clubs="user.clubsNeedingCountry" @saved="…" @snoozed="…"/>
import {computed, ref, watch} from 'vue';
import TlDialog from '~/components/TlDialog.vue';
import CountryField from '~/components/clubs/CountryField.vue';
import AvatarImageLoader from '~/components/loaders/AvatarImageLoader.vue';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {endpoints} from '~/network/endpoints.js';
import {countryInSentence} from '~/utils/countryNames';
import {
  COUNTRY_LINE, COUNTRY_LINE_MANY, COUNTRY_SAVED, COUNTRIES_SAVED, COUNTRY_TITLE, COUNTRY_TITLE_MANY,
  allSameQuestion, applyCountryResults, canSaveCountries, countryAnswerBody, countryPromptMode, countryRows, rowsUnchanged,
} from '~/utils/clubCountry';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  /** auth/me clubsNeedingCountry rows: {clubId, clubName, imageFileName, suggestedCountryId, suggestedCountryName, suggestedCountryCode}. */
  clubs: {type: Array, default: () => []},
  /** The sign-in question offers Not now (24 hours); the Needs you row only closes. */
  notNow: {type: Boolean, default: true},
});
const emit = defineEmits(['update:modelValue', 'saved', 'snoozed']);

const {httpRequest} = useHttpRequest();
const {setAlertMessage} = useAlertService();

const rows = ref([]);
const mode = ref('single');
const choosing = ref(false);
const editing = ref(null);
const saving = ref(false);
const error = ref(null);

const reset = () => {
  rows.value = countryRows(props.clubs);
  mode.value = countryPromptMode(rows.value);
  choosing.value = mode.value === 'pick';
  editing.value = null;
  error.value = null;
};
watch(() => props.modelValue, (open) => { if (open) reset(); }, {immediate: true});

const many = computed(() => rows.value.length > 1);
const first = computed(() => rows.value[0] ?? null);
const title = computed(() => (many.value ? COUNTRY_TITLE_MANY : first.value ? COUNTRY_TITLE(first.value.clubName) : ''));
const canSave = computed(() => canSaveCountries(rows.value));
const oneTapMany = computed(() => mode.value === 'allSame' && rowsUnchanged(rows.value));
const saveLabel = computed(() => (oneTapMany.value ? 'Yes, all of them' : 'Save'));

const initials = (name) => (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const close = () => emit('update:modelValue', false);

const finish = (message) => {
  setAlertMessage({message, color: 'success'});
  emit('saved');
  close();
};

const save = async () => {
  if (!canSave.value || saving.value) return;
  saving.value = true;
  error.value = null;
  try {
    if (!many.value) {
      const row = first.value;
      const res = await httpRequest(endpoints.clubs.country(row.clubId), {countryId: row.countryId});
      // Another admin answered first: the club keeps theirs; say so, and nothing is left to ask.
      finish(res?.alreadySet && res?.message ? `${res.message}.` : COUNTRY_SAVED);
      return;
    }
    const res = await httpRequest(endpoints.clubs.countries, countryAnswerBody(rows.value));
    const left = applyCountryResults(rows.value, res?.clubs);
    if (left.every((r) => r.settled)) {
      finish(left.length ? `${COUNTRIES_SAVED}. ${left.map((r) => `${r.clubName}: ${r.message}`).join('. ')}.` : COUNTRIES_SAVED);
      return;
    }
    // Some rows still need something (a country, or they were refused): they stay, with their own words.
    rows.value = left.filter((r) => !r.settled);
    mode.value = countryPromptMode(rows.value) === 'allSame' ? 'mixed' : countryPromptMode(rows.value);
    emit('saved');
  } catch (e) {
    error.value = e?.global?.[0] ?? e?.countryId?.[0] ?? "Couldn't save the country. Try again.";
  } finally {
    saving.value = false;
  }
};

// One tap: "Yes" on the single question saves the suggestion as it stands.
const yes = () => save();

const snooze = async () => {
  const ids = rows.value.map((r) => r.clubId);
  close();
  try {
    await httpRequest(endpoints.clubs.countrySnooze, {clubIds: ids});
  } catch {
    // The dialog is already closed: a failed write only means the question may come back sooner.
  }
  emit('snoozed', ids);
};

const onClose = (open) => {
  if (open) return;
  if (props.notNow) snooze();
  else close();
};

const setRowCountry = (row, c) => { row.country = c ?? row.country; };
</script>

<template>
  <tl-dialog :model-value="modelValue" :title="title" :max-width="many ? 520 : 440" persistent close-on-escape
             :close-label="notNow ? 'Not now' : 'Close'" @update:model-value="onClose">
    <p class="ccd-line">{{ many ? COUNTRY_LINE_MANY : COUNTRY_LINE }}</p>

    <!-- One club -->
    <template v-if="!many && first">
      <p v-if="mode === 'single' && !choosing" class="ccd-question">
        Is <strong>{{ first.clubName }}</strong> in <strong>{{ countryInSentence(first.country) }}</strong>?
      </p>
      <div v-else>
        <div class="tl-label mb-2">Country</div>
        <country-field v-model="first.countryId" :known="first.country" :autofocus="choosing && mode === 'single'"
                       @country="(c) => setRowCountry(first, c)"/>
      </div>
    </template>

    <!-- Several clubs: one row each; the value opens the picker for that row only -->
    <template v-else>
      <p v-if="oneTapMany" class="ccd-question">
        Are all {{ rows.length }} clubs in <strong>{{ countryInSentence(rows[0]?.country) }}</strong>?
      </p>
      <div class="ccd-rows" :aria-label="oneTapMany ? allSameQuestion(rows) : 'Each club\'s country'">
        <div v-for="row in rows" :key="row.clubId" class="ccd-row">
          <div class="ccd-row-main">
            <avatar-image-loader v-if="row.imageFileName" :image-file-name="row.imageFileName" :image-clickable="false"
                                 rounded="lg" size="32" class="ccd-crest"/>
            <span v-else class="ccd-crest ccd-crest--initials">{{ initials(row.clubName) }}</span>
            <span class="ccd-name">{{ row.clubName }}</span>
            <button type="button" class="ccd-value" :class="{'ccd-value--empty': !row.countryId}"
                    :aria-expanded="editing === row.clubId" @click="editing = editing === row.clubId ? null : row.clubId">
              <span>{{ row.countryId ? row.country?.name : 'Choose country' }}</span>
              <v-icon size="18" :icon="editing === row.clubId ? 'mdi-chevron-up' : 'mdi-chevron-down'"/>
            </button>
          </div>
          <div v-if="editing === row.clubId" class="ccd-row-pick">
            <country-field v-model="row.countryId" :known="row.country" autofocus density="compact"
                           @country="(c) => setRowCountry(row, c)"/>
          </div>
          <div v-if="row.message" class="ccd-row-msg">{{ row.message }}</div>
        </div>
      </div>
    </template>

    <div v-if="error" class="ccd-error">{{ error }}</div>

    <template #actions>
      <v-btn v-if="notNow" variant="text" class="ccd-act" :disabled="saving" @click="snooze">Not now</v-btn>
      <v-btn v-else variant="text" class="ccd-act" :disabled="saving" @click="close">Cancel</v-btn>
      <v-spacer/>
      <template v-if="!many && mode === 'single' && !choosing">
        <!-- X222 (R91-L1): phones say "Change" (as the Stripe country step does) so Not now / Change / Yes stay on one line at 360 -->
        <v-btn variant="text" class="ccd-act" :disabled="saving" @click="choosing = true"><span class="d-none d-sm-inline">Choose another</span><span class="d-sm-none">Change</span></v-btn>
        <v-btn color="primary" class="px-6 ccd-act" :loading="saving" @click="yes">Yes</v-btn>
      </template>
      <v-btn v-else color="primary" class="px-6 ccd-act" :disabled="!canSave" :loading="saving" @click="save">{{ saveLabel }}</v-btn>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
.ccd-line {
  font-size: 14px;
  line-height: 1.5;
  color: rgb(var(--v-theme-textSubtle));
  margin: 0 0 14px;
}

.ccd-question {
  font-size: 16px;
  line-height: 1.45;
  margin: 0 0 6px;
}

.ccd-rows {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
}

.ccd-row + .ccd-row {
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.ccd-row-main {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
}

.ccd-crest {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.ccd-crest--initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
}

.ccd-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  overflow: hidden;
  /* X221 (R90 L-5): a long club name wraps to two lines instead of being cut after a word */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow-wrap: anywhere;
}

.ccd-value {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  max-width: 50%;
  padding: 4px 6px 4px 10px;
  border-radius: 8px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
  cursor: pointer;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover,
  &:focus-visible {
    border-color: rgb(var(--v-theme-primary));
  }
}

.ccd-value--empty {
  color: rgb(var(--v-theme-primary));
}

.ccd-row-pick {
  padding: 0 12px 12px;
}

.ccd-row-msg {
  padding: 0 12px 10px 54px;
  font-size: 12.5px;
  color: rgb(var(--v-theme-warning));
}

/* X221 (R90 L-5): phones put the country under the name (both whole), and the three buttons fit one line */
@media (max-width: 599px) {
  .ccd-row-main {
    flex-wrap: wrap;
    row-gap: 6px;
  }

  .ccd-name {
    flex-basis: calc(100% - 42px);
  }

  .ccd-value {
    max-width: calc(100% - 42px);
    margin-left: 42px;
  }

  .ccd-act {
    padding-inline: 10px !important;
    min-width: 0;
  }
}

.ccd-error {
  margin-top: 12px;
  font-size: 13px;
  color: rgb(var(--v-theme-error));
}
</style>
